/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useMemo} from 'react'

import {getDisplayPrice} from '../utils/product-utils'
import {useCurrentCustomer} from './use-current-customer'

/**
 * @typedef {object} ProductPricing
 * @property {string} currency
 * @property {number} price
 * @property {number} displayPrice
 * @property {boolean} hasDiscount
 * @property {boolean} isCustomNetPriceEnabled
 */

/**
 * Gets the product pricing, allowing for extensibility using custom hooks
 * @param {object} product
 * @param {boolean} [isBonusProduct]
 * @return {ProductPricing}
 */
function useProductPricing(product, isBonusProduct = false) {
    const {isCustomNetPriceEnabled} = useCurrentCustomer().data

    return useMemo(() => {
        if (!product) return null
        const {price, displayPrice, hasDiscount, isFree} = getDisplayPrice(
            product,
            isBonusProduct,
            isCustomNetPriceEnabled
        )
        return {
            currency: product.currency,
            price,
            displayPrice,
            hasDiscount,
            isFree
        }
    }, [product, isBonusProduct, isCustomNetPriceEnabled])
}

export default useProductPricing
